// batch-65 inkoop-kosten (FO-483 §3.C) — inzicht in kosten en marge: per klant
// (relatie-tab Facturatie, alleen met margerecht), per werkbon en per project
// (blok "Inkoopkosten"). Aggregaties altijd server-side over de volledige set.
// Niets hiervan gaat ooit naar het klantportaal.

import { z } from 'zod'
import type { InkoopDoorbelastStatus } from './inkoop-doorbelasten'
import type { ToewijzingDoeltype } from './kostendrager'

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik de vorm jjjj-mm-dd.')

/** Periode voor "Kosten en marge" (van/tot inclusief, NL-kalenderdagen). */
export const klantKostenEnMargeQuerySchema = z
  .object({
    van: ymd,
    tot: ymd,
    /** '1' = de dag-cache overslaan (knop "Vernieuwen"). */
    vernieuw: z.enum(['0', '1']).optional(),
  })
  .refine((d) => d.van <= d.tot, { message: 'De einddatum ligt voor de begindatum.', path: ['tot'] })
export type KlantKostenEnMargeQuery = z.infer<typeof klantKostenEnMargeQuerySchema>

export interface KostenPerHoofdgroepDTO {
  hoofdgroepCode: string | null
  hoofdgroepNaam: string
  bedrag: number
  soorten: { kostensoortId: string | null; kostensoortNaam: string; bedrag: number }[]
}

/** "Kosten en marge" van een klant over een periode. */
export interface KlantKostenEnMargeDTO {
  clientId: string
  van: string
  tot: string
  /** Verkoopfacturen (excl. btw, uitgegeven, incl. creditnota's) met factuurdatum in de periode. */
  omzet: number
  /** Aan deze klant (of zijn werkbonnen/projecten) toegerekende inkoop, factuurdatum in de periode. */
  toegerekendeKosten: number
  /** Daarvan doorbelast: verkoopbedrag en het inkoopdeel. */
  doorbelastVerkoop: number
  doorbelastInkoop: number
  /** Nog te doorbelasten (toegerekend op regels met status te doorbelasten, minus al doorbelast). */
  nogTeDoorbelasten: number
  /** Omzet min toegerekende inkoop (exclusief uren). */
  marge: number
  margePct: number | null
  /** Aantal toegerekende inkoopregels (doorklik). */
  aantalRegels: number
  perHoofdgroep: KostenPerHoofdgroepDTO[]
  /** Moment van berekenen (dag-cache). */
  berekendOp: string
}

export const inkoopkostenQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
})
export type InkoopkostenQuery = z.infer<typeof inkoopkostenQuerySchema>

/** Eén toegerekende inkoopregel in het blok "Inkoopkosten" (werkbon/project). */
export interface InkoopkostenRegelDTO {
  regelId: string
  factuurId: string
  factuurNr: string
  /** Factuurdatum (yyyy-MM-dd, Europe/Amsterdam). */
  factuurdatum: string
  leverancierNaam: string
  omschrijving: string
  kostensoortNaam: string | null
  doeltype: ToewijzingDoeltype
  /** "Werkbon WB2600417" of "Project PRJ-2026-001 ..." */
  doelNaam: string
  /** Toegerekend bedrag (excl. btw). */
  bedrag: number
  doorbelastStatus: InkoopDoorbelastStatus | null
  /**
   * Doorbelast verkoopbedrag voor dit doel (actieve doorbelastingen). Alleen met
   * margerecht; anders null (inkoop naast verkoop is de marge).
   */
  doorbelastVerkoop: number | null
}

export interface InkoopkostenOverzichtDTO {
  regels: InkoopkostenRegelDTO[]
  totaalInkoop: number
  /** Alleen met margerecht; anders null. */
  totaalDoorbelastVerkoop: number | null
  /** Gefactureerde omzet; alleen met margerecht, anders null. */
  omzet: number | null
  /** Omzet min toegerekende inkoop (exclusief uren); alleen met margerecht. */
  marge: number | null
  margePct: number | null
  meta: { total: number; page: number; pageSize: number }
}
